import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import { usePettyCashPaymentPurposes, useDeletePettyCashPaymentPurpose } from "hooks/usePettyCashMaster"

import PettyCashPaymentPurposeAddEdit from "./PettyCashPaymentPurposeAddEdit"

const PettyCashPaymentPurposeList = () => {
  document.title = "Petty Cash Payment Purposes"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({ page: 1, search: "" })
  const queryParams = useMemo(() => ({ page: params.page || 1, per_page: 10, search: params.search }), [params])

  const { data: responseData, isLoading, refetch, isRefetching } = usePettyCashPaymentPurposes(queryParams)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeletePettyCashPaymentPurpose()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) { setSelectedData(null); setIsEdit(false) }
  }, [modal])

  const handleAddNewClick = () => { setSelectedData(null); setIsEdit(false); setModal(true) }
  const handleEditClick = item => { setSelectedData(item); setIsEdit(true); setModal(true) }
  const handleDeleteClick = item => { setSelectedData(item); setDeleteModal(true) }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Payment purpose deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch {
      toast.error("Failed to delete payment purpose.")
    }
  }

  const columns = useMemo(() => [
    { header: "ID", accessorKey: "id" },
    { header: "Name", accessorKey: "name" },
    { header: "Slug", accessorKey: "slug" },
    {
      header: "Sub-categories",
      cell: ({ row }) => {
        const children = row.original.children || []
        return children.length > 0
          ? <Badge color="info">{children.length} sub-items</Badge>
          : <span className="text-muted small">—</span>
      },
    },
    { header: "Description", cell: ({ row }) => row.original.description || "-" },
    {
      header: "Action",
      cell: ({ row }) => (
        <ul className="list-unstyled hstack gap-1 mb-0">
          <CanView access={[ACCESS.PETTY_CASH_PAYMENT_PURPOSE.EDIT]}>
            <li>
              <Button color="info" size="sm" onClick={() => handleEditClick(row.original)} id={`edit-pcpp-${row.original.id}`}>
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`edit-pcpp-${row.original.id}`}>Edit</UncontrolledTooltip>
            </li>
          </CanView>
          <CanView access={[ACCESS.PETTY_CASH_PAYMENT_PURPOSE.DELETE]}>
            <li>
              <Button color="danger" size="sm" onClick={() => handleDeleteClick(row.original)} id={`delete-pcpp-${row.original.id}`}>
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`delete-pcpp-${row.original.id}`}>Delete</UncontrolledTooltip>
            </li>
          </CanView>
        </ul>
      ),
    },
  ], [])

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Petty Cash" breadcrumbItem="Payment Purposes">
      <div className="d-flex align-items-center mb-3">
        <h5 className="mb-0 mt-3 card-title flex-grow-1">
          Total <Badge className="badge-soft-info">{paginationInfo?.total ?? 0}</Badge>
        </h5>
        <div className="flex-shrink-0">
          <CanView access={[ACCESS.PETTY_CASH_PAYMENT_PURPOSE.ADD]}>
            <Button color="primary" onClick={handleAddNewClick}>Add New</Button>
          </CanView>
          <Button color="light" onClick={refetch} className="ms-1">
            <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
          </Button>
          {hasActiveFilters && <Button color="danger" onClick={resetParams} className="ms-1">Reset Filters</Button>}
        </div>
      </div>

      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <Row className="mb-3">
        <Col sm={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <PettyCashPaymentPurposeAddEdit modal={modal} isEdit={isEdit} editedData={selectedData} toggleModal={toggleModal} />
    </PageWrapper>
  )
}

export default PettyCashPaymentPurposeList
